/**
 * One source of truth for how code LOOKS and INDENTS in every Monaco editor.
 *
 * Before this, each editor carried its own options: fontSize drifted between
 * 13, 14 and 16; two of them named fonts that are not bundled (JetBrains Mono,
 * Fira Code) and so fell back to whatever the machine happened to have; and
 * only two set the indentation options at all — the rest inherited Monaco's
 * `detectIndentation: true`, which infers the indent from the buffer and
 * quietly overrides `tabSize`. A candidate could type a solution that indented
 * one way during the test and read back another way when a recruiter opened it.
 *
 * Spread these into an editor's `options`. Anything genuinely editor-specific
 * (readOnly, minimap, scrollbar, height) stays at the call site.
 */

/**
 * A system stack rather than a bundled web font on purpose: these editors run
 * on candidate machines over networks we do not control, and a font arriving
 * late would reflow the code mid-test.
 */
export const EDITOR_FONT_FAMILY =
  'Menlo, Monaco, "Cascadia Mono", "Segoe UI Mono", "Roboto Mono", Consolas, "Courier New", monospace';

export const EDITOR_FONT_SIZE = 14;

/**
 * Touch devices get 16px: iOS Safari zooms the whole viewport when a focused
 * control's text is smaller than that, which throws a candidate out of their
 * layout mid-answer.
 */
export const EDITOR_FONT_SIZE_MOBILE = 16;

export const EDITOR_LINE_HEIGHT = 21;

/**
 * One indent width for every language, matching the online compilers
 * candidates practise on.
 */
export const EDITOR_TAB_SIZE = 4;

export const getEditorFontOptions = (isMobile = false) => ({
  fontFamily: EDITOR_FONT_FAMILY,
  fontSize: isMobile ? EDITOR_FONT_SIZE_MOBILE : EDITOR_FONT_SIZE,
  lineHeight: EDITOR_LINE_HEIGHT,
  // Off deliberately: a ligature draws `!=` as `≠` and `->` as an arrow, which
  // is the wrong thing to show someone being assessed on the syntax.
  fontLigatures: false,
});

export const EDITOR_INDENT_OPTIONS = {
  tabSize: EDITOR_TAB_SIZE,
  insertSpaces: true,
  // Monaco defaults this ON and then guesses the indent from the existing
  // buffer, silently overriding the tabSize above.
  detectIndentation: false,
  // Off so Tab always indents. With it on, Tab accepts the open autocomplete
  // suggestion instead — the "Tab doesn't work" complaint.
  tabCompletion: "off",
  autoIndent: "full",
  trimAutoWhitespace: true,
};

/** Font and indentation together, for editors with nothing special to add. */
export const getBaseEditorOptions = (isMobile = false) => ({
  ...getEditorFontOptions(isMobile),
  ...EDITOR_INDENT_OPTIONS,
});
